import { memo, useCallback, useId, useMemo, useRef } from 'react';
import Badge from '../Badge.jsx';
import FilterChip from './FilterChip.jsx';
import FilterDropdown from './FilterDropdown.jsx';
import {
  FILTERS_CHIP_BASE,
  FILTERS_FIELD_BASE,
} from './filtersTokens.js';
import { ChevronDown } from 'lucide-react';

const stopPropagation = (e) => e.stopPropagation();

const VISIBLE_CHIP_DEFAULT = 2;

/**
 * One filter slot — Figma Text field (7996:5502).
 * Multi-select: informative Tag chips with × inside the field; overflow as "+N" chip (no ×).
 *
 * @param {object} props
 * @param {import('./filtersTypes.js').FilterSlotConfig} props.config
 * @param {boolean} props.isOpen
 * @param {(id: string) => void} props.onOpen
 * @param {() => void} props.onClose
 * @param {(message: string) => void} props.onAnnounce
 * @param {(label: string) => string} props.removeAriaLabel
 * @param {(label: string) => string} props.removedAnnounce
 * @param {(label: string) => string} props.addedAnnounce
 * @param {(count: number) => string} props.moreChip
 */
const FilterSlot = ({
  config,
  isOpen,
  onOpen,
  onClose,
  onAnnounce,
  removeAriaLabel,
  removedAnnounce,
  addedAnnounce,
  moreChip,
}) => {
  const {
    id,
    label,
    multiple = true,
    value = [],
    onChange,
    options = [],
    disabled = false,
    maxVisible = VISIBLE_CHIP_DEFAULT,
  } = config;

  const fieldRef = useRef(null);
  const listboxId = useId();

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );
  const hasSelection = value.length > 0;
  const chipLimit = multiple ? maxVisible : 1;
  const visibleChips = useMemo(
    () => selectedOptions.slice(0, chipLimit),
    [selectedOptions, chipLimit],
  );
  const hiddenCount = multiple ? selectedOptions.length - visibleChips.length : 0;
  const hasHidden = hiddenCount > 0;

  const handleChange = useCallback(
    (next) => {
      const added = next.filter((v) => !value.includes(v));
      const removed = value.filter((v) => !next.includes(v));
      onChange?.(next);

      added.forEach((v) => {
        const opt = options.find((o) => o.value === v);
        if (opt) onAnnounce?.(addedAnnounce(opt.label));
      });
      removed.forEach((v) => {
        const opt = options.find((o) => o.value === v);
        if (opt) onAnnounce?.(removedAnnounce(opt.label));
      });
    },
    [value, onChange, options, onAnnounce, addedAnnounce, removedAnnounce],
  );

  const handleRemove = useCallback(
    (removedValue) => handleChange(value.filter((v) => v !== removedValue)),
    [handleChange, value],
  );

  const handleFieldClick = useCallback(() => {
    if (disabled) return;
    if (isOpen) onClose?.();
    else onOpen?.(id);
  }, [disabled, id, isOpen, onClose, onOpen]);

  const handleFieldKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen) onClose?.();
      else onOpen?.(id);
    }
  }, [disabled, id, isOpen, onClose, onOpen]);

  return (
    <div className="relative inline-flex">
      <div
        ref={fieldRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-disabled={disabled || undefined}
        aria-label={hasSelection ? `${label}, ${value.length} selected` : label}
        onClick={handleFieldClick}
        onKeyDown={handleFieldKeyDown}
        className={FILTERS_FIELD_BASE}
      >
        {!hasSelection ? (
          <span className="min-w-0 flex-1 truncate">{label}</span>
        ) : (
          <div className="flex min-w-max flex-nowrap items-center gap-1">
            {visibleChips.map((opt) => (
              <FilterChip
                key={`${id}-${opt.value}`}
                value={opt.value}
                label={opt.label}
                onRemove={handleRemove}
                removeAriaLabel={removeAriaLabel}
                disabled={disabled}
              />
            ))}
            {hasHidden ? (
              <span
                className="inline-flex shrink-0"
                onClick={stopPropagation}
                onKeyDown={stopPropagation}
              >
                <Badge
                  variant="informative"
                  size="medium"
                  className={FILTERS_CHIP_BASE}
                  aria-label={`${hiddenCount} more ${label} selected`}
                >
                  {moreChip(hiddenCount)}
                </Badge>
              </span>
            ) : null}
          </div>
        )}

        <ChevronDown
          size={24}
          className={[
            'ml-auto shrink-0 text-content-muted transition-transform',
            isOpen ? 'rotate-180' : '',
          ].join(' ')}
          aria-hidden
        />
      </div>

      <FilterDropdown
        options={options}
        value={value}
        onChange={handleChange}
        multiple={multiple}
        open={isOpen}
        onClose={onClose}
        triggerRef={fieldRef}
        listboxId={listboxId}
      />
    </div>
  );
};

export default memo(FilterSlot);
